import { css, useTheme } from '@emotion/react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface ConfirmRehearsalDialogProps {
  isOpen: boolean;
  projectId: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmRehearsalDialog({
  isOpen,
  projectId,
  onConfirm,
  onCancel,
}: ConfirmRehearsalDialogProps) {
  const theme = useTheme();

  if (!isOpen) return null;

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        background: ${theme.colors.overlay};
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
      `}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-rehearsal-title"
        css={css`
          background: ${theme.colors.surface};
          border: 1px solid ${theme.colors.border};
          border-radius: ${radii.md};
          padding: ${spacing.xl};
          max-width: 440px;
          width: 90%;
          box-shadow: ${shadows.prominent};
        `}
      >
        <h2
          id="confirm-rehearsal-title"
          css={css`
            margin: 0 0 ${spacing.sm};
            font-size: ${fontSizes.xl};
            color: ${theme.colors.text};
          `}
        >
          No Rehearsal Yet
        </h2>
        <p
          css={css`
            margin: 0 0 ${spacing.xl};
            color: ${theme.colors.textSecondary};
            font-size: ${fontSizes.md};
            line-height: 1.5;
          `}
        >
          You haven&apos;t rehearsed this speech yet. Music durations will use estimated timings
          instead of your actual pace. Generate anyway?
        </p>
        <div
          css={css`
            display: flex;
            gap: ${spacing.sm};
            justify-content: flex-end;
          `}
        >
          <Link
            to={`/app/projects/${projectId}/rehearse`}
            onClick={onCancel}
            css={css`
              display: inline-flex;
              align-items: center;
              padding: ${spacing.sm} ${spacing.md};
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.md};
              background: ${theme.colors.surface};
              color: ${theme.colors.text};
              font-size: ${fontSizes.sm};
              text-decoration: none;
              &:hover {
                border-color: ${theme.colors.text};
              }
            `}
          >
            Rehearse First
          </Link>
          <Button shape="rect" size="sm" onClick={onConfirm}>
            Generate Anyway
          </Button>
        </div>
      </div>
    </div>
  );
}
